import { Suspense } from 'react'
import { AnimatePresence } from 'framer-motion'
import { Route, Switch } from 'react-router-dom'
import { Admin } from '@admin'
import { EditListRoute as EditList } from '@admin/template-elements/edit-list/routes'
import { PermissionedRoute } from '@authed/permissioned.route'
import { FeatureDict, selectFeatureIncludes } from '@authed/selectors'
import { PageWithNav } from '@components/layout/pageWithNav'
import { Loader } from '@components/loader'
import { UserPartial } from '@components/user/user-menu'
import { CreateDatasetProvider } from '@datasets/reducer/context'
import { lazyImport } from '@global/utils/lazyImport'
import { useAppSelector } from '@redux/hooks'
import { Submissions } from '@submissions'
import { UploadTranslationStepper } from '@taxonomy-translation/upload-translation-stepper'
import { Templates } from '@templates'
import { Home } from 'home'
import { Dashboard } from 'pages/dashboard'
import { DatasetsContextProvider } from 'pages/datasets/context'
import { Reports } from 'reports'
import { ROUTES } from 'routes'
import { TaxonomyTranslationMapping } from 'taxonomy-translation'
import { Navigation } from './navigation'
import { TopBar } from '../topBar'
import { Main } from './components/main'

const { Datasets } = lazyImport(() => import(/* webpackChunkName: "datasets" */ 'pages/datasets'))

// Do not call as an anonomous function on the Route directly,
// this causes the CreateDatasetProvider to unmount, which results
// in some internals triggering an infinite loop.
const CodesRoute = () => {
  return (
    <CreateDatasetProvider>
      <Submissions />
    </CreateDatasetProvider>
  )
}

export const PageLayout = () => {
  const user = useAppSelector(state => state.Auth.user)
  const [
    DATASETS_PAGE_ENABLED = false,
    DATA_STANDARDS_DASHBOARD = false,
    TAXONOMY_TRANSLATION_MAPPING = false,
  ] = useAppSelector(
    selectFeatureIncludes([
      FeatureDict.DATASETS_PAGE,
      FeatureDict.DATA_STANDARDS_DASHBOARD,
      FeatureDict.TAXONOMY_TRANSLATION_MAPPING,
    ]),
  )

  // FIXME: Temporary flipped ROI_ENABLED to show the route.

  const userPartial: UserPartial = {
    name: user?.name ?? '',
    role: user?.role ?? null,
    avatar_slug: user?.avatar_slug ?? null,
    super_admin: user?.super_admin ?? null,
  }

  return (
    <PageWithNav>
      <PageWithNav.Nav>
        {(props: { toggle: () => void; getMenuExpandedStatus: () => boolean }) => (
          <Navigation
            toggleNavExpansion={props.toggle}
            getMenuExpandedStatus={props.getMenuExpandedStatus}
          />
        )}
      </PageWithNav.Nav>
      <PageWithNav.Page>
        <TopBar user={userPartial} bgColor="light" />
        <Main>
          <AnimatePresence exitBeforeEnter initial={false}>
            <Switch>
              {DATASETS_PAGE_ENABLED ? (
                <Route exact path="/">
                  <Suspense fallback={<Loader />}>
                    <DatasetsContextProvider>
                      <Datasets />
                    </DatasetsContextProvider>
                  </Suspense>
                </Route>
              ) : (
                <Route exact path="/" component={Home} />
              )}
              <Route path="/codes" component={CodesRoute} />
              <Route exact path="/reports" component={Reports} />
              {DATA_STANDARDS_DASHBOARD ? (
                <PermissionedRoute exact path="/dashboard" component={Dashboard} />
              ) : null}
              <PermissionedRoute path="/template/:id" component={Templates} />
              <PermissionedRoute exact path="/admin/lists/edit/:id" component={EditList} />
              {TAXONOMY_TRANSLATION_MAPPING ? (
                <PermissionedRoute
                  path={ROUTES.TAXONOMY_TRANSLATION_UPLOAD}
                  component={UploadTranslationStepper}
                />
              ) : null}
              {TAXONOMY_TRANSLATION_MAPPING ? (
                <PermissionedRoute
                  path={ROUTES.TAXONOMY_TRANSLATION}
                  component={TaxonomyTranslationMapping}
                />
              ) : null}
              <PermissionedRoute path="/admin" component={Admin} />
            </Switch>
          </AnimatePresence>
        </Main>
      </PageWithNav.Page>
    </PageWithNav>
  )
}
